/**
 * Stopping a browser-side recording.
 *
 * A browser-decoded tile (HLS — the DJI aircraft — or WebRTC) has no
 * server-side frame to record, so REC there drives a `MediaRecorder` over
 * `video.captureStream()` and uploads the blob on stop. That stop has two
 * failure modes, and before this module both of them stranded the button in
 * REC for ever, because the state reset sat after an `await` that had thrown:
 *
 *   1. The recorder is already `inactive`. A MediaRecorder ends itself when the
 *      tracks of the captured element end, which is what a tile remount does —
 *      a feed reconnect, a `videoKey` bump, a source switch. `.stop()` then
 *      throws InvalidStateError.
 *   2. `.stop()` is accepted but `onstop` never arrives, so the promise never
 *      settles and the caller waits for ever.
 *
 * Both also silently threw away everything buffered so far. So the rule is:
 * never call `.stop()` on a recorder that cannot take it, always upload what is
 * in hand, and never let either outcome decide whether the UI returns to idle.
 */

/** The three states a MediaRecorder reports. */
export type RecorderState = 'inactive' | 'recording' | 'paused';

/** How long `onstop` may take before we give up waiting and upload the buffer
 *  ourselves. Long enough for an ordinary flush of a multi-minute recording,
 *  short enough that a wedged recorder does not read as a frozen button. */
export const ONSTOP_TIMEOUT_MS = 5_000;

export interface StopPlan {
  /** Call `recorder.stop()`. False when it would throw, so the caller must not. */
  stop: boolean;
  /** Upload the buffered chunks directly, because `onstop` can never fire. */
  salvage: boolean;
}

/**
 * What to do with a recording the operator has asked to stop.
 *
 * `bufferedBytes` is the size of everything `ondataavailable` has handed over
 * so far. Footage already in hand is worth uploading even when the recorder
 * that produced it is gone — that is the difference between a short recording
 * and a lost one.
 */
export function planStop(state: RecorderState | undefined, bufferedBytes: number): StopPlan {
  if (state === 'recording' || state === 'paused') {
    // Live: stop it and let `onstop` deliver the final chunk and upload.
    return { stop: true, salvage: false };
  }
  // Gone. Nothing will fire, so the only footage that survives is what we hold.
  return { stop: false, salvage: bufferedBytes > 0 };
}
